import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { applyLineReviewUpdate } from "./lineReview";
import { ApiError } from "../../api/http";
import {
  createEventFromImport,
  createInvoiceImportFromDraft,
  getInvoiceImport,
  loadAllInvoiceImportQueueItems,
  markInvoiceImportReady,
  patchInvoiceDraft,
  patchInvoiceLine,
  uploadInvoiceSourceDocument,
} from "../../api/invoiceImportApi";
import { invoiceExampleCatalog } from "./catalog";
import { runBatchParseQueue } from "./batchParseQueue";
import { catalogImportSource, uploadImportSource } from "./invoiceSource";
import { parseInvoiceFromSource } from "./parseInvoiceFromSource";
import {
  type InvoiceImportCandidate,
  type ParsedInvoiceDraft,
  type ParsedInvoiceLine,
} from "./types";

export type SaveState = "idle" | "saving" | "saved" | "error";

type InvoiceImportContextValue = {
  queueItems: QueueItemView[];
  queueLoading: boolean;
  catalogSelection: Set<string>;
  isBatchParsing: boolean;
  batchParseTotal: number;
  batchParseCompleted: number;
  pendingUploadCount: number;
  saveState: SaveState;
  saveError: string | null;
  toggleCatalogSelection: (filename: string) => void;
  selectAllCatalog: () => void;
  clearCatalogSelection: () => void;
  addUploads: (files: FileList | File[]) => void;
  parseSelected: () => Promise<void>;
  refreshQueue: () => Promise<void>;
  loadImport: (id: string) => Promise<InvoiceImportCandidate | undefined>;
  getItem: (id: string) => InvoiceImportCandidate | undefined;
  updateDraft: (id: string, draft: ParsedInvoiceDraft) => void;
  updateLine: (id: string, lineId: string, patch: Partial<ParsedInvoiceLine>) => void;
  markReady: (id: string) => Promise<void>;
  createEventFromImport: (id: string) => Promise<{ eventId: string }>;
  isCatalogImported: (filename: string) => boolean;
};

export type QueueItemView = InvoiceImportCandidate & { isImported: boolean };

const InvoiceImportContext = createContext<InvoiceImportContextValue | null>(null);

function isPdfFile(file: File): boolean {
  return file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
}

function isImportedItem(item: InvoiceImportCandidate): boolean {
  return Boolean(item.importedEventId);
}

type PendingUpload = {
  filename: string;
  bytes: ArrayBuffer;
};

export function InvoiceImportProvider({ children }: { children: ReactNode }) {
  const [queueById, setQueueById] = useState<Record<string, InvoiceImportCandidate>>({});
  const [queueLoading, setQueueLoading] = useState(true);
  const [catalogSelection, setCatalogSelection] = useState<Set<string>>(() => new Set());
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [isBatchParsing, setIsBatchParsing] = useState(false);
  const [batchParseTotal, setBatchParseTotal] = useState(0);
  const [batchParseCompleted, setBatchParseCompleted] = useState(0);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const draftSaveTimersRef = useRef<Map<string, number>>(new Map());
  const savedResetTimerRef = useRef<number | null>(null);

  const queueItems = useMemo<QueueItemView[]>(
    () => Object.values(queueById).map((item) => ({ ...item, isImported: isImportedItem(item) })),
    [queueById],
  );

  const refreshQueue = useCallback(async () => {
    setQueueLoading(true);
    try {
      const items = await loadAllInvoiceImportQueueItems();
      setQueueById(Object.fromEntries(items.map((item) => [item.id, item])));
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshQueue();
  }, [refreshQueue]);

  const setItem = useCallback((item: InvoiceImportCandidate) => {
    setQueueById((current) => ({ ...current, [item.id]: item }));
  }, []);

  const markSaved = useCallback(() => {
    setSaveState("saved");
    setSaveError(null);
    if (savedResetTimerRef.current) window.clearTimeout(savedResetTimerRef.current);
    savedResetTimerRef.current = window.setTimeout(() => setSaveState("idle"), 1800);
  }, []);

  const isCatalogImported = useCallback(
    (filename: string) =>
      queueItems.some(
        (item) => item.source.filename === filename && Boolean(item.importedEventId),
      ),
    [queueItems],
  );

  const toggleCatalogSelection = useCallback(
    (filename: string) => {
      if (isCatalogImported(filename)) return;
      setCatalogSelection((current) => {
        const next = new Set(current);
        if (next.has(filename)) next.delete(filename);
        else next.add(filename);
        return next;
      });
    },
    [isCatalogImported],
  );

  const selectAllCatalog = useCallback(() => {
    setCatalogSelection(
      new Set(
        invoiceExampleCatalog
          .map((entry) => entry.filename)
          .filter((filename) => !isCatalogImported(filename)),
      ),
    );
  }, [isCatalogImported]);

  const clearCatalogSelection = useCallback(() => {
    setCatalogSelection(new Set());
  }, []);

  const addUploads = useCallback((files: FileList | File[]) => {
    void (async () => {
      const fileArray = Array.from(files).filter(isPdfFile);
      if (fileArray.length === 0) return;

      const additions: PendingUpload[] = [];
      for (const file of fileArray) {
        const duplicateActive = queueItems.some(
          (item) => item.source.filename === file.name && !item.importedEventId,
        );
        const duplicatePending = pendingUploads.some((entry) => entry.filename === file.name);
        if (duplicateActive || duplicatePending) continue;
        additions.push({ filename: file.name, bytes: await file.arrayBuffer() });
      }
      if (additions.length === 0) return;
      setPendingUploads((current) => [...current, ...additions]);
    })();
  }, [pendingUploads, queueItems]);

  const persistParsedImport = useCallback(
    async (source: InvoiceImportCandidate["source"], draft: ParsedInvoiceDraft, pdfBytes: ArrayBuffer) => {
      const created = await createInvoiceImportFromDraft({ source, draft });
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      await uploadInvoiceSourceDocument(created.id, blob, source.filename);
      const refreshed = await getInvoiceImport(created.id);
      setItem(refreshed);
      return refreshed;
    },
    [setItem],
  );

  const parseSelected = useCallback(async () => {
    type ParseJob = { key: string; run: () => Promise<void> };
    const jobs: ParseJob[] = [];

    for (const filename of catalogSelection) {
      if (isCatalogImported(filename)) continue;
      const entry = invoiceExampleCatalog.find((item) => item.filename === filename);
      if (!entry) continue;
      jobs.push({
        key: `catalog:${filename}`,
        run: async () => {
          const source = catalogImportSource(entry);
          const response = await fetch(entry.url);
          const bytes = await response.arrayBuffer();
          const draft = await parseInvoiceFromSource(source, bytes);
          await persistParsedImport(source, draft, bytes);
        },
      });
    }

    for (const pending of pendingUploads) {
      jobs.push({
        key: `upload:${pending.filename}`,
        run: async () => {
          const source = uploadImportSource(pending.filename);
          const draft = await parseInvoiceFromSource(source, pending.bytes);
          await persistParsedImport(source, draft, pending.bytes);
        },
      });
    }

    if (jobs.length === 0) return;

    setIsBatchParsing(true);
    setBatchParseTotal(jobs.length);
    setBatchParseCompleted(0);

    try {
      await runBatchParseQueue(
        jobs.map((job) => job.key),
        async (key) => {
          const job = jobs.find((entry) => entry.key === key);
          if (!job) return;
          try {
            await job.run();
          } catch (cause) {
            throw cause;
          } finally {
            setBatchParseCompleted((count) => count + 1);
          }
        },
      );
      setPendingUploads([]);
      setCatalogSelection(new Set());
      await refreshQueue();
    } finally {
      setIsBatchParsing(false);
    }
  }, [catalogSelection, isCatalogImported, pendingUploads, persistParsedImport, refreshQueue]);

  const loadImport = useCallback(
    async (id: string) => {
      try {
        const item = await getInvoiceImport(id);
        setItem(item);
        return item;
      } catch {
        return undefined;
      }
    },
    [setItem],
  );

  const getItem = useCallback((id: string) => queueById[id], [queueById]);

  const updateDraft = useCallback((id: string, draft: ParsedInvoiceDraft) => {
    const existing = queueById[id];
    if (!existing || existing.importedEventId) return;

    setItem({ ...existing, draft, markedReady: false });
    setSaveState("saving");
    setSaveError(null);

    const existingTimer = draftSaveTimersRef.current.get(id);
    if (existingTimer) window.clearTimeout(existingTimer);

    const timer = window.setTimeout(() => {
      void patchInvoiceDraft(id, draft)
        .then((saved) => {
          setItem(saved);
          markSaved();
        })
        .catch((error) => {
          setSaveState("error");
          setSaveError(error instanceof ApiError ? error.message : "Failed to save review changes.");
        });
    }, 450);

    draftSaveTimersRef.current.set(id, timer);
  }, [markSaved, queueById, setItem]);

  const updateLine = useCallback((id: string, lineId: string, patch: Partial<ParsedInvoiceLine>) => {
    const existing = queueById[id];
    if (!existing?.draft || existing.importedEventId) return;

    const updatedLine = applyLineReviewUpdate(
      existing.draft.lines.find((entry) => entry.id === lineId)!,
      patch,
    );

    const nextDraft: ParsedInvoiceDraft = {
      ...existing.draft,
      lines: existing.draft.lines.map((line) =>
        line.id === lineId ? { ...line, ...patch } : line,
      ),
    };

    setItem({ ...existing, draft: nextDraft, markedReady: false });
    setSaveState("saving");
    setSaveError(null);

    void patchInvoiceLine(id, lineId, {
      name: patch.name,
      description: patch.description,
      interpretedUnit: patch.interpretedUnit,
      interpretedQuantity: patch.interpretedQuantity,
      classification: patch.classification,
      needsReview: updatedLine.needsReview,
    })
      .then((saved) => {
        setItem(saved);
        markSaved();
      })
      .catch((error) => {
        setSaveState("error");
        setSaveError(error instanceof ApiError ? error.message : "Failed to save line changes.");
      });
  }, [markSaved, queueById, setItem]);

  const markReady = useCallback(async (id: string) => {
    const saved = await markInvoiceImportReady(id);
    setItem(saved);
    const refreshed = await getInvoiceImport(id);
    setItem(refreshed);
  }, [setItem]);

  const createEvent = useCallback(async (id: string) => {
    const result = await createEventFromImport(id);
    await refreshQueue();
    return result;
  }, [refreshQueue]);

  const value = useMemo<InvoiceImportContextValue>(() => ({
    queueItems,
    queueLoading,
    catalogSelection,
    isBatchParsing,
    batchParseTotal,
    batchParseCompleted,
    pendingUploadCount: pendingUploads.length,
    saveState,
    saveError,
    toggleCatalogSelection,
    selectAllCatalog,
    clearCatalogSelection,
    addUploads,
    parseSelected,
    refreshQueue,
    loadImport,
    getItem,
    updateDraft,
    updateLine,
    markReady,
    createEventFromImport: createEvent,
    isCatalogImported,
  }), [
    addUploads,
    batchParseCompleted,
    batchParseTotal,
    catalogSelection,
    clearCatalogSelection,
    createEvent,
    getItem,
    isBatchParsing,
    isCatalogImported,
    loadImport,
    markReady,
    parseSelected,
    pendingUploads.length,
    queueItems,
    queueLoading,
    refreshQueue,
    saveError,
    saveState,
    selectAllCatalog,
    toggleCatalogSelection,
    updateDraft,
    updateLine,
  ]);

  return <InvoiceImportContext.Provider value={value}>{children}</InvoiceImportContext.Provider>;
}

export function useInvoiceImport() {
  const context = useContext(InvoiceImportContext);
  if (!context) throw new Error("useInvoiceImport must be used within InvoiceImportProvider.");
  return context;
}

export function formatApiConflictMessage(error: ApiError): string {
  switch (error.code) {
    case "duplicate_import":
      return "This invoice has already been imported.";
    case "source_document_locked":
      return "This invoice source is locked because it has already been used to create an Event.";
    case "reviewed_import_locked":
      return "This invoice has already been imported and can no longer be edited.";
    default:
      return error.message;
  }
}
